/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { QueryState } from '../../components/ui/query-state.js';
import { useAdminDashboard } from '../../lib/queries/dashboard.js';

import { RecentActivityCard } from './dashboard-activity.js';
import { FollowUpAlertsCard } from './dashboard-follow-ups.js';
import {
  DistributionCard,
  PerformanceCard,
  RevenueSessionsCard,
  UpcomingCard,
} from './dashboard-sections.js';
import { StatsRow } from './dashboard-stats.js';

/** Admin → Dashboard Overview (Figma). Live, server-computed clinic metrics. */
export function AdminDashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const dashboard = useAdminDashboard();

  return (
    <div className="p-8">
      <QueryState
        isLoading={dashboard.isLoading}
        isError={dashboard.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel=""
      >
        {dashboard.data !== undefined && (
          <div className="space-y-6">
            <StatsRow stats={dashboard.data.stats} />

            <div className="grid gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <RevenueSessionsCard monthly={dashboard.data.monthly} />
              </div>
              <DistributionCard distribution={dashboard.data.distribution} />
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <PerformanceCard rows={dashboard.data.therapistPerformance} />
              <UpcomingCard sessions={dashboard.data.upcomingToday} />
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <FollowUpAlertsCard />
              <RecentActivityCard items={dashboard.data.recentActivity} />
            </div>
          </div>
        )}
      </QueryState>
    </div>
  );
}
